import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authServiceApi } from "../api/authService";
import { STORAGE_KEYS } from "../utils/constants";

const getInitialState = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.AUTH);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        user: parsed.user || null,
        token: parsed.token || null,
        refreshToken: parsed.refreshToken || null,
        role: parsed.role || null,
        uid: parsed.uid || null,
        expiresIn: parsed.expiryTime || null,
        isAuthenticated: !!parsed.token,
        initializing: true,
        loading: false,
        error: null,
      };
    }
  } catch {
    // ignore parse errors
  }
  return {
    user: null,
    token: null,
    refreshToken: null,
    role: null,
    uid: null,
    expiresIn: null,
    isAuthenticated: false,
    initializing: true,
    loading: false,
    error: null,
  };
};

export const loginAdmin = createAsyncThunk(
  "auth/loginAdmin",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await authServiceApi.login(email, password);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Login failed");
    }
  }
);

export const loginEmployee = createAsyncThunk(
  "auth/loginEmployee",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await authServiceApi.login(email, password);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Login failed");
    }
  }
);

export const logout = createAsyncThunk("auth/logout", async (_, { rejectWithValue }) => {
  try {
    await authServiceApi.logout();
  } catch (error) {
    return rejectWithValue(error.message || "Logout failed");
  }
});

const authSlice = createSlice({
  name: "auth",
  initialState: getInitialState(),
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setAuthReady: (state) => {
      state.initializing = false;
    },
    setCredentials: (state, action) => {
      const { token, refreshToken, role, uid, email, expiryTime } = action.payload;
      state.token = token;
      state.refreshToken = refreshToken;
      state.role = role;
      state.uid = uid;
      state.user = { email, uid };
      state.expiresIn = expiryTime;
      state.isAuthenticated = true;
      state.initializing = false;
      state.error = null;

      localStorage.setItem(
        STORAGE_KEYS.AUTH,
        JSON.stringify({ token, refreshToken, role, uid, user: { email, uid }, expiryTime })
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.role = action.payload.role;
        state.uid = action.payload.uid;
        state.user = { email: action.payload.email, uid: action.payload.uid };
        state.expiresIn = action.payload.expiryTime;
        state.isAuthenticated = true;
        state.initializing = false;
        state.error = null;

        localStorage.setItem(
          STORAGE_KEYS.AUTH,
          JSON.stringify({
            token: action.payload.token,
            refreshToken: action.payload.refreshToken,
            role: action.payload.role,
            uid: action.payload.uid,
            user: { email: action.payload.email, uid: action.payload.uid },
            expiryTime: action.payload.expiryTime,
          })
        );
      })
      .addCase(loginAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.initializing = false;
      })
      .addCase(loginEmployee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginEmployee.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.role = action.payload.role;
        state.uid = action.payload.uid;
        state.user = { email: action.payload.email, uid: action.payload.uid };
        state.expiresIn = action.payload.expiryTime;
        state.isAuthenticated = true;
        state.initializing = false;
        state.error = null;

        localStorage.setItem(
          STORAGE_KEYS.AUTH,
          JSON.stringify({
            token: action.payload.token,
            refreshToken: action.payload.refreshToken,
            role: action.payload.role,
            uid: action.payload.uid,
            user: { email: action.payload.email, uid: action.payload.uid },
            expiryTime: action.payload.expiryTime,
          })
        );
      })
      .addCase(loginEmployee.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.initializing = false;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.role = null;
        state.uid = null;
        state.expiresIn = null;
        state.isAuthenticated = false;
        state.initializing = false;
        state.error = null;

        localStorage.removeItem(STORAGE_KEYS.AUTH);
        localStorage.removeItem(STORAGE_KEYS.TOKEN);
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.USER);
      })
      .addCase(logout.rejected, (state, action) => {
        state.error = action.payload;
        state.initializing = false;
      });
  },
});

export const { clearError, setCredentials, setAuthReady } = authSlice.actions;
export default authSlice.reducer;
